import { watchEffect } from 'vue';

/**
 * 리스트 + 선택값 동기화 guard
 *
 * string|number ID 기반으로 동작한다.
 * editableIds === undefined → 미설정 상태 (defaultList 표시 중)
 * editableIds === []        → 사용자가 의도적으로 전체 제거한 상태
 *
 * Rule 1: editable 미설정(undefined) & selected가 defaultList에 없음 → editable 맨 앞에 추가
 * Rule 2: editable 있음 & selected가 fixed∪editable에 없음 → selected를 첫 번째 값으로 업데이트
 * Rule 3: fixed∪editable 모두 비어있음(editable = []) → selected 초기화
 * Rule 5: selected 없음 & editable에 아이템 추가됨 → 첫 번째 아이템 자동 선택
 *
 * @param {object} options
 * @param {() => string|number|undefined}        options.getSelectedId
 * @param {() => (string|number)[]|undefined}    options.getEditableIds   undefined = 미설정
 * @param {() => (string|number)[]}              options.getFixedIds
 * @param {() => (string|number)[]}              options.getDefaultIds
 * @param {(ids: (string|number)[]) => void}     options.emitUpdateList
 * @param {(id: string|number) => void}          options.emitUpdateSelected
 */
export function useFieldGuard({
    getSelectedId,
    getEditableIds,
    getFixedIds,
    getDefaultIds,
    emitUpdateList,
    emitUpdateSelected,
}) {
    watchEffect(() => {

        const selectedId = getSelectedId();
        const editableIds = getEditableIds(); // undefined | string[]
        const fixedIds = getFixedIds();

        const isUnset = editableIds === undefined;
        const editable = editableIds ?? [];

        // Rule 3: fixed∪editable 모두 비어있음 → selected 초기화
        if (!isUnset && editable.length === 0 && fixedIds.length === 0) {
            if (selectedId) emitUpdateSelected('');
            return;
        }

        // Rule 5: selected 없음 & editable에 아이템 있음 → 첫 번째 자동 선택
        if (!selectedId) {
            if (editable.length > 0 && fixedIds.length === 0) {
                emitUpdateSelected(editable[0]);
            }
            return;
        }

        const strSelected = String(selectedId);
        const inFixed = fixedIds.some((id) => String(id) === strSelected);

        if (isUnset) {
            // Rule 1: 미설정 상태 → selected가 defaultList에 없으면 editable 맨 앞에 추가
            const defaultIds = getDefaultIds();
            const inDefault = defaultIds.some((id) => String(id) === strSelected);
            if (!inFixed && !inDefault) {
                emitUpdateList([selectedId]);
            }
        } else {
            // Rule 2: editable 있는데 selected가 fixed∪editable에 없음 → 첫 번째 값으로 업데이트
            const inEditable = editable.some((id) => String(id) === strSelected);
            if (!inFixed && !inEditable) {
                const newSelected = fixedIds[0] ?? editable[0] ?? '';
                emitUpdateSelected(newSelected);
            }
        }
    });
}
